import React, { useEffect, useRef } from "react";

import {
  ENTRY_DATA_SECTION_KEYS,
  IconDefaultXMark,
  IconTrash,
} from "@frontend/common";

import { DealLaScSection } from "@/types/dealLaSc.types";
import TableCreatorDragButton from "@FillInTableContainer/shared/components/TableCreatorDragButton/TableCreatorDragButton";
import { TableCreatorShellList } from "@FillInTableContainer/shared/types/tableCreator.types";
import { isAddDeleteSubGroupShell } from "@FillInTableContainer/shared/utils/getOptionsFromData";

import DealLaScRightsScopeShell from "./components/DealLaScRightsScopeShell/DealLaScRightsScopeShell";
import SubGroupShellDividerPlusBar from "./components/SubGroupShellDividerPlusBar/SubGroupShellDividerPlusBar";
import * as S from "./DealLaScRightsScopeSubGroupShell.style";
import { useHandleDealLaScRightsScopeTableCreatorSubGroupShell } from "./hooks/useHandleDealLaScRightsScopeTableCreatorSubGroupShell";

export interface PositionInfoProps {
  groupTableKey: DealLaScSection;
  groupTableIndex: number;
  groupShellKey: string | null;
  groupShellIndex: number;
  tableKey: string | null;
  tableIndex: number;
  subGroupShellKey: string | null;
  subGroupShellIndex: number;
  subGroupShellCount: number;
}

interface Props extends PositionInfoProps {
  subGroupShellCount: number;
  shellList: TableCreatorShellList;
  isFocusedGroupShell: boolean;
  isClearGroupShell: boolean;
  isDeleteGroupShell: boolean;
  isAlertGroupShell: boolean;
  isFocusGroupShellContainer: boolean;
  handleDragStart: (
    event: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => void;
  handleDragEnd: (groupTableKey: string) => void;
  handleDragOn: () => void;
}

const isDragSubGroupShell: string[] = ["Rights Scope"];

const DealLaScRightsScopeSubGroupShell = ({
  groupShellIndex,
  groupShellKey,
  groupTableIndex,
  groupTableKey,
  tableIndex,
  tableKey,
  subGroupShellCount,
  subGroupShellIndex,
  subGroupShellKey,
  shellList,
  isAlertGroupShell,
  isClearGroupShell,
  isDeleteGroupShell,
  isFocusedGroupShell,
  isFocusGroupShellContainer,
  handleDragEnd,
  handleDragOn,
  handleDragStart,
}: Props) => {
  const positionPropsInfo = {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    subGroupShellKey,
    subGroupShellIndex,
    tableKey,
    tableIndex,
  };

  const articleSection = ENTRY_DATA_SECTION_KEYS.dealsLaSc;
  const selectButtonRef = useRef<HTMLDivElement>(null);

  const {
    focusStatus,
    isFocused,
    isEnableEdit,
    isClearSubGroupShell,
    isDeleteSubGroupShell,
    isLastSubGroupShell,
    isFocusedAddButton,
    handleClickToDeleteSubGroupShell,
    handleFocusSubGroupShell,
    clickAddSubGroupShellButton,
    handleDefaultFocus,
    changeFocusStatus,
    changePositionInfo,
    changePositionInfoToDefault,
  } = useHandleDealLaScRightsScopeTableCreatorSubGroupShell({
    positionPropsInfo,
    subGroupShellCount,
  });

  const isPossibleSelected = !!groupShellKey;

  useEffect(() => {
    const handleCheckClickOutsideShell = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('[data-dropdown="list-box"]'))
        return;
      if (isFocused) {
        const target = e.target as HTMLElement;
        const clickSelectButton = selectButtonRef.current?.contains(target);

        if (clickSelectButton) return;
        if (target.dataset.button) return;

        handleDefaultFocus();
      }
    };

    window.addEventListener("click", handleCheckClickOutsideShell);

    return () =>
      window.removeEventListener("click", handleCheckClickOutsideShell);
  }, [handleDefaultFocus, isFocused]);

  const changeFocusStatusForDragging = () => {
    changePositionInfo();
    changeFocusStatus({
      type: "dragging",
      container: "subGroupShell",
    });
  };

  const changeFocusStatusForDragEnd = () => {
    changePositionInfoToDefault();
    changeFocusStatus({
      type: "default",
      container: "noFocus",
    });
  };

  const isDisplayClearDeleteButton =
    (isClearSubGroupShell || isDeleteSubGroupShell) && isEnableEdit;

  const isDraggingSubGroupShell =
    isEnableEdit && isDragSubGroupShell.includes(subGroupShellKey || "");

  return (
    <>
      <S.GroupShellOutsideWrapper>
        {isDisplayClearDeleteButton && (
          <S.DeleteTableButtonWrapper $isDragging={isDraggingSubGroupShell}>
            <S.DeleteTableButton
              data-button="delete-button"
              onClick={(e) => handleClickToDeleteSubGroupShell(e)}
            >
              {focusStatus.type === "clear" ? (
                <IconDefaultXMark color="#feddd2" />
              ) : null}
              {focusStatus.type === "delete" ? (
                <IconTrash color="#feddd2" />
              ) : null}
            </S.DeleteTableButton>
          </S.DeleteTableButtonWrapper>
        )}
        <S.GroupShellWrapper
          isFocused={isFocused}
          isDragging={isFocused && focusStatus.type === "dragging"}
        >
          <S.SelectWrapper isPossibleSelected={isPossibleSelected}>
            <S.GroupShellSelectButtonDividerWrapper />

            {isDraggingSubGroupShell && (
              <S.DragButton
                data-button="drag-button"
                ref={selectButtonRef}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  changeFocusStatusForDragging();
                }}
                onMouseUp={(e) => {
                  e.stopPropagation();
                  handleFocusSubGroupShell(e);
                }}
                onDragStart={(e) => {
                  if (!(subGroupShellCount > 1)) return;
                  handleDragStart(e, subGroupShellIndex);
                  changeFocusStatusForDragging();
                }}
                onDragEnd={() => {
                  if (!(subGroupShellCount > 1)) return;
                  if (changeFocusStatusForDragEnd) {
                    handleDragEnd(subGroupShellKey || "");
                    changeFocusStatusForDragEnd();
                  }
                }}
                onDrag={() => {
                  if (!(subGroupShellCount > 1)) return;
                  handleDragOn();
                }}
                draggable={subGroupShellCount > 1}
              >
                <TableCreatorDragButton
                  dragContainer="subGroupShell"
                  isDragging={isFocused && focusStatus.type === "dragging"}
                  onMouseDown={(e) => {
                    e.stopPropagation();

                    changeFocusStatusForDragging();
                  }}
                  onMouseUp={(e) => {
                    e.stopPropagation();
                    handleFocusSubGroupShell(e);
                  }}
                  className="rights-scope-sub-group-shell-drag-button"
                />
              </S.DragButton>
            )}
            <S.GroupShell
              $isFocused={isDisplayClearDeleteButton}
              $isDeleted={
                isDisplayClearDeleteButton && focusStatus.type === "delete"
              }
            >
              {isAddDeleteSubGroupShell(articleSection, subGroupShellKey) && (
                <SubGroupShellDividerPlusBar
                  dividerType="TABLE_ROW"
                  onClick={(event) => {
                    clickAddSubGroupShellButton(event, {
                      tableIndexParameter: tableIndex,
                      groupShellIndexParameter: groupShellIndex,
                      groupShellKeyParameter: groupShellKey,
                      subGroupShellIndexParameter: subGroupShellIndex,
                      subGroupShellKeyParameter: subGroupShellKey,
                    });
                  }}
                  isLastPosition={false}
                  isVisibility
                  subGroupShellKey={subGroupShellKey}
                  style={{
                    width: "100%",
                    position: "absolute",
                    left: "0",
                    top: "-0.4rem",
                  }}
                />
              )}
              {shellList.map(({ shellKey }, shellListIndex) => {
                return (
                  <DealLaScRightsScopeShell
                    key={`shellList-${String(shellListIndex)}`}
                    groupTableKey={groupTableKey}
                    groupTableIndex={groupTableIndex}
                    groupShellKey={groupShellKey}
                    groupShellIndex={groupShellIndex}
                    subGroupShellIndex={subGroupShellIndex}
                    subGroupShellKey={subGroupShellKey}
                    shellKey={shellKey}
                    shellIndex={0}
                    tableKey={tableKey}
                    tableIndex={tableIndex}
                    isFocusedGroupShell={isFocusedGroupShell}
                    isClearGroupShell={isClearGroupShell}
                    isDeleteGroupShell={isDeleteGroupShell}
                    isAlertGroupShell={isAlertGroupShell}
                    isClearSubGroupShell={isClearSubGroupShell}
                    isDeleteSubGroupShell={isDeleteSubGroupShell}
                    isFocusGroupShellContainer={isFocusGroupShellContainer}
                  />
                );
              })}
            </S.GroupShell>
          </S.SelectWrapper>
        </S.GroupShellWrapper>
        {isAddDeleteSubGroupShell(articleSection, subGroupShellKey) &&
          isLastSubGroupShell && (
            <SubGroupShellDividerPlusBar
              onClick={(event) => {
                clickAddSubGroupShellButton(event, {
                  tableIndexParameter: tableIndex,
                  groupShellIndexParameter: groupShellIndex,
                  groupShellKeyParameter: groupShellKey,
                  subGroupShellIndexParameter: subGroupShellIndex + 1,
                  subGroupShellKeyParameter: subGroupShellKey,
                });
              }}
              isLastPosition
              isVisibility={isFocusedAddButton}
              subGroupShellKey={subGroupShellKey}
              dividerType="TABLE_ROW"
              style={{
                width: "100%",
                position: "absolute",
                left: "0",
                top: "auto",
                bottom: "-0rem",
              }}
            />
          )}
      </S.GroupShellOutsideWrapper>{" "}
    </>
  );
};

export default DealLaScRightsScopeSubGroupShell;
